import { State } from '../../core/reducers';
import { createSelector } from '@ngrx/store';
import {
  selectMonthlyScenarioStatesPerYearMonth
} from './scenario.reducer';
import { Dictionary } from '@ngrx/entity';
import {
  AccountCategory, AccountType,
  createRowNode,
  MonthlyAccounts
} from '../model/accounting.model';
import {
  selectYearMonthSpan
} from './span.selector';
import {
  selectAccountHierarchy,
  selectAccountNumbers,
  selectOpenedAccounts,
} from './accounting.selector';
import {FlowTableLevel, FlowTableRow, MonthlyGraphData, ROW_TYPE} from '../model/flow-table-row.model';

export const getScenarioState = (state: State) => state.scenarioState;

export const selectMonthlyScenarioAccounts = createSelector(
  getScenarioState,
  selectMonthlyScenarioStatesPerYearMonth
);

export const selectMonthlyScenarioAccountsWithinExistingSpan = createSelector(
  selectYearMonthSpan,
  selectMonthlyScenarioAccounts,
  (yearMonths: string[], monthlyStates: Dictionary<MonthlyAccounts>): MonthlyAccounts[] => {
    return yearMonths.reduce((accounts: MonthlyAccounts[], yearMonth) => {
      const monthlyAccounts = monthlyStates[yearMonth];
      if (monthlyAccounts) {
        accounts.push(monthlyAccounts);
      }
      return accounts;
    }, []);
  });

export const selectScenarioTableData = createSelector(
  selectYearMonthSpan,
  selectAccountHierarchy,
  selectMonthlyScenarioAccountsWithinExistingSpan,
  selectOpenedAccounts,
  (yearMonthSpan, rootNode, monthlyAccounts: MonthlyAccounts[], openedAccounts: number[]): FlowTableRow[] | undefined => {
    if (rootNode && monthlyAccounts.length === yearMonthSpan.length) {
      const rootNodes = createRowNode(rootNode, 0, monthlyAccounts, openedAccounts).children;
      return [
        rootNodes[0], {...rootNodes[0], children: [], rowType: ROW_TYPE.recap},
      ];
    } else {
      return undefined;
    }
  }
);

export const selectScenarioTableDataWithMargin = createSelector(
  selectScenarioTableData,
  (rows?: FlowTableRow[]): FlowTableRow[] | undefined => {
    if (!rows) {
      return undefined;
    }

    const operatingRows = rows[0].children;
    const revenueRow = operatingRows.find(row => row.accountNo === 3);
    const costsGoodsAndServicesRow = operatingRows.find(row => row.accountNo === 4);
    const costsStaffRow = operatingRows.find(row => row.accountNo === 5);
    const costsOtherRow = operatingRows.find(row => row.accountNo === 6);
    const resultRow = rows[1];

    const firstGrossMarginValues = revenueRow?.values?.map((_, index) => revenueRow?.values[index] - (costsGoodsAndServicesRow?.values[index] ?? 0)) ?? [];
    const firstGrossMarginTotal = (revenueRow?.total ?? 0) - (costsGoodsAndServicesRow?.total ?? 0);

    const secondGrossMarginValues = firstGrossMarginValues.map((value, index) => value - (costsStaffRow?.values[index] ?? 0));
    const secondGrossMarginTotal = firstGrossMarginTotal - (costsStaffRow?.total ?? 0);

    const ebitdaValues = secondGrossMarginValues.map((value, index) => value - (costsOtherRow?.values[index] ?? 0));
    const ebitdaTotal = secondGrossMarginTotal - (costsOtherRow?.total ?? 0);

    const firstGrossMarginRow: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-1',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: firstGrossMarginValues,
      total: firstGrossMarginTotal,
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: false,
      depth: 1
    };
    const firstGrossMarginRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-1',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: firstGrossMarginValues.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total: firstGrossMarginTotal / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: true,
      depth: 1
    };
    const firstMarginCalculationIndex = operatingRows.findIndex(row => row.accountNo === 5);
    if (firstMarginCalculationIndex >= 0) {
      operatingRows.splice(firstMarginCalculationIndex, 0, firstGrossMarginRow, firstGrossMarginRowPercent);
    }

    const secondGrossMarginRow: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-2',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: secondGrossMarginValues,
      total: secondGrossMarginTotal,
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: false,
      depth: 1
    };
    const secondGrossMarginRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'gross-margin-2',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: secondGrossMarginValues.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total: secondGrossMarginTotal / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: true,
      depth: 1
    };
    const secondMarginCalculationIndex = operatingRows.findIndex(row => row.accountNo === 6);
    if (secondMarginCalculationIndex >= 0) {
      operatingRows.splice(secondMarginCalculationIndex, 0, secondGrossMarginRow, secondGrossMarginRowPercent);
    }

    const ebitdaRow: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'ebitda',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: ebitdaValues,
      total: ebitdaTotal,
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: false,
      depth: 1
    };
    const ebitdaRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'ebitda',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: ebitdaValues.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total: ebitdaTotal / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.intermediary,
      percent: true,
      depth: 1
    };
    const ebitdaCalculationIndex = operatingRows.findIndex(row => row.accountNo === 7);
    if (ebitdaCalculationIndex >= 0) {
      operatingRows.splice(ebitdaCalculationIndex, 0, ebitdaRow, ebitdaRowPercent);
    }

    const resultRowPercent: FlowTableRow = {
      cat: AccountCategory.operating,
      type: AccountType.variation,
      name: 'result-operating',
      accountNo: 0,
      level: FlowTableLevel.first,
      opened: false,
      color: null,
      values: resultRow.values.map((value, index) => value / (revenueRow?.values[index] ?? 1)),
      total: resultRow.total / (revenueRow?.total ?? 1),
      children: [],
      rowType: ROW_TYPE.recap,
      percent: true,
      depth: 1
    };
    rows[1].children.push(resultRowPercent)

    return rows;
  }
);

export const selectScenarioInputData = createSelector(
  selectScenarioTableData,
  (tableData: FlowTableRow[] | undefined): number[] => {
    if (tableData && tableData.length) {
      const length = tableData[0].values.length
      const inputRows = [
        tableData[0].children[0].values,
        // tableData[1].values // TODO properly handle these values
      ];
      return inputRows.reduce((cur, acc) => {
          return cur.map((value, index) => acc[index] + value)
        }
        , Array.from({length}).fill(0) as number[]);
    }
    return [];
  }
);

export const selectScenarioOutputData = createSelector(
  selectScenarioTableData,
  (tableData: FlowTableRow[] | undefined): number[] => {
    if (tableData && tableData.length) {
      const length = tableData[0].values.length
      const inputRows = [
        tableData[0].children[1].values,
        tableData[0].children[2].values,
        tableData[0].children[3].values,
        // tableData[0].children[4].values, // TODO properly handle these values
        // tableData[0].children[5].values, // TODO properly handle these values
        // tableData[2].values // TODO properly handle these values
      ];
      return inputRows.reduce((cur, acc) => {
          return cur.map((value, index) => acc[index] + value)
        }
        , Array.from({length}).fill(0) as number[]);
    }
    return [];
  }
);

export const selectScenarioTreasuryData = createSelector(
  selectScenarioTableData,
  (tableData: FlowTableRow[] | undefined): FlowTableRow | undefined => {
    if (tableData && tableData.length) {
      return tableData[tableData.length - 1];
    }
    return undefined;
  }
);

export const selectScenarioGraphData = createSelector(
  selectScenarioInputData,
  selectScenarioOutputData,
  selectScenarioTreasuryData,
  (cashIn, cashOut, treasuryRows) => {
    const result = cashIn.map((value, index) => value - cashOut[index]);
    const treasury = treasuryRows?.values ?? [];
    return {
      input: cashIn,
      output: cashOut,
      result,
      treasury
    } as MonthlyGraphData;
  }
);

export const selectScenarioData = createSelector(
  selectAccountNumbers,
  selectYearMonthSpan,
  selectMonthlyScenarioAccounts,
  (accounts: number[], yearMonths: string[], monthlyAccounts: Dictionary<MonthlyAccounts>): number[][] => {
    return accounts.map((_, index) =>
      yearMonths.map(yearMonth => monthlyAccounts[yearMonth]?.accountsValues[index].value || 0 )
    );
  });

export const selectScenarios = createSelector(
  getScenarioState,
  (state) => state.scenarios
);

export const selectCurrentScenarioId = createSelector(
  getScenarioState,
  (state) => state.currentScenarioId
);
